
import { Component, Input, OnChanges } from '@angular/core';
import {
  formatAmmViewLogDateTime,
  toAmmViewLogTimestamp,
} from './format-view-log-datetime';

export interface ViewLogItem {
  actionType: string;
  actorName?: string;
  actionOn?: string | Date;
  description?: string;
}

interface ViewLogDisplayItem {
  actionType: string;
  actorName: string;
  actionOnText: string;
  actionTime: number;
  description: string;
}

@Component({
  selector: 'doc-view-log',
  standalone: true,
  imports: [],
  templateUrl: './view-log.component.html',
  styleUrls: ['./view-log.component.scss'],
})
export class ViewLogComponent implements OnChanges {
  @Input() title = 'Activity Timeline';
  @Input() logs: ViewLogItem[] = [];
  @Input() entityType: 'file' | 'folder' = 'file';
  @Input() createdBy = '-';
  @Input() createdOn = '-';
  @Input() updatedBy = '-';
  @Input() updatedOn = '-';

  displayLogs: ViewLogDisplayItem[] = [];

  ngOnChanges(): void {
    this.displayLogs = this.buildDisplayLogs();
  }

  trackByLog(index: number, log: ViewLogDisplayItem): string {
    return `${log.actionType}-${log.actorName}-${log.actionOnText}-${index}`;
  }

  private buildDisplayLogs(): ViewLogDisplayItem[] {
    const providedLogs = (this.logs || [])
      .map((log) => this.toDisplayLog(log))
      .filter((log): log is ViewLogDisplayItem => !!log);

    const fallbackLogs = this.buildFallbackLogs();
    const merged = [...providedLogs, ...fallbackLogs];
    const deduped = this.deduplicateTimelineEntries(merged);

    return deduped.sort((a, b) => b.actionTime - a.actionTime);
  }

  /**
   * Renames/moves also bump Last Modified, which previously produced a synthetic "Updated / metadata updated"
   * row alongside "Renamed". Drop redundant updated/metadata rows when a structural action exists nearby.
   */
  private deduplicateTimelineEntries(logs: ViewLogDisplayItem[]): ViewLogDisplayItem[] {
    /** Same server request sets Renamed + modified-on, or Created + Duplicated for copies; keep tight. */
    const SAME_BURST_MS = 15_000;

    const isStructuralChange = (log: ViewLogDisplayItem): boolean =>
      /^(renamed|moved|duplicated)$/i.test(String(log.actionType).trim());

    const isMetadataOnlyUpdateRow = (log: ViewLogDisplayItem): boolean => {
      if (/^updated$/i.test(String(log.actionType).trim())) {
        return true;
      }
      return /metadata updated/i.test(String(log.description));
    };

    const isCreatedTimelineRow = (log: ViewLogDisplayItem): boolean => {
      if (!/^created$/i.test(String(log.actionType).trim())) {
        return false;
      }
      const d = String(log.description).trim().toLowerCase();
      return d === 'folder created' || d === 'file created';
    };

    return logs.filter((log) => {
      if (isMetadataOnlyUpdateRow(log)) {
        const overlapsStructural = logs.some(
          (other) =>
            other !== log &&
            isStructuralChange(other) &&
            Math.abs(other.actionTime - log.actionTime) <= SAME_BURST_MS
        );
        if (overlapsStructural) {
          return false;
        }
      }

      if (isCreatedTimelineRow(log)) {
        const duplicateNearby = logs.some((other) => {
          if (other === log || !/^duplicated$/i.test(String(other.actionType).trim())) {
            return false;
          }
          const closeByTime =
            Math.abs(other.actionTime - log.actionTime) <= SAME_BURST_MS;
          const sameDisplayedInstant =
            log.actionOnText !== '-' &&
            log.actionOnText === other.actionOnText;
          return closeByTime || sameDisplayedInstant;
        });
        if (duplicateNearby) {
          return false;
        }
      }

      return true;
    });
  }

  private buildFallbackLogs(): ViewLogDisplayItem[] {
    const itemLabel = this.entityType === 'folder' ? 'Folder' : 'File';
    const fallbackLogs: ViewLogDisplayItem[] = [];

    // Do not synthesize "Updated" here: folder/file metadata changes are logged in UserActivityLog
    // (FOLDER_UPDATE / FILE_UPDATE, FOLDER_RENAME / FILE_RENAME, etc.) and returned by getViewDownloadLogs.
    // A synthetic Updated row duplicated Renamed and real update rows because updatedOn changes on every save.

    if (this.hasValue(this.createdOn) || this.hasValue(this.createdBy)) {
      fallbackLogs.push(
        this.toDisplayLog({
          actionType: 'Created',
          actorName: this.createdBy,
          actionOn: this.createdOn,
          description: `${itemLabel} created`,
        })
      );
    }

    return fallbackLogs.filter((log): log is ViewLogDisplayItem => !!log);
  }

  private toDisplayLog(log: ViewLogItem): ViewLogDisplayItem | null {
    if (!log || !this.hasValue(log.actionType)) {
      return null;
    }

    const actionOnText = this.formatDateText(log.actionOn);

    return {
      actionType: String(log.actionType),
      actorName: this.hasValue(log.actorName) ? String(log.actorName) : '-',
      actionOnText,
      actionTime: this.toActionTime(log.actionOn),
      description: this.hasValue(log.description) ? String(log.description) : '',
    };
  }

  private formatDateText(value: string | Date | undefined): string {
    if (!this.hasValue(value)) {
      return '-';
    }

    return formatAmmViewLogDateTime(value, this.entityType);
  }

  private toActionTime(value: string | Date | undefined): number {
    if (!this.hasValue(value)) {
      return 0;
    }

    return toAmmViewLogTimestamp(value, this.entityType);
  }

  private hasValue(value: unknown): boolean {
    return value !== undefined && value !== null && String(value).trim() !== '' && String(value).trim() !== '-';
  }
}
